import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { updateScreenTitle } from "../../../LayoutContainers/AppLayout/redux/AppSlice";
import AppService from "../../../Configurations/Appservice";
import { People, CheckCircle, Pending, Payments, CalendarMonth, AccountBalance } from "@mui/icons-material";

// Helper function to format currency
const formatCurrency = (amount, currency = 'AED') => {
  if (amount === null || amount === undefined) return 'AED 0.00';
  return new Intl.NumberFormat('en-AE', { style: 'currency', currency }).format(amount);
};

// Metric Tile Component - matching other commission pages
const MetricTile = ({ title, amount, subtitle, icon: Icon, color }) => (
  <Card
    sx={{
      background: color,
      borderRadius: 2,
      p: 2.5,
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      minHeight: 130
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: 12,
        right: 12,
        opacity: 0.15,
        color: 'white'
      }}
    >
      <Icon sx={{ fontSize: 42 }} />
    </Box>
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <Typography
        variant="subtitle2"
        sx={{
          color: 'rgba(255, 255, 255, 0.95)',
          fontWeight: 500,
          mb: 1.5,
          fontSize: '0.75rem'
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="h4"
        sx={{
          color: '#fff',
          fontWeight: 700,
          mb: 0.75,
          fontSize: '1.4rem',
          lineHeight: 1.2
        }}
      >
        {amount}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(255, 255, 255, 0.85)',
          fontSize: '0.65rem',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        {subtitle}
      </Typography>
    </Box>
  </Card>
);

const MonthlyStatement = () => {
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(updateScreenTitle("Monthly Commission Statement"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  useEffect(() => {
    fetchMonthlyStatement();
  }, []);

  const fetchMonthlyStatement = async () => {
    try {
      setLoading(true);
      const authInfo = localStorage.getItem("authInfo");
      const parsedAuth = JSON.parse(authInfo);
      const partnerId = parsedAuth.principalInfo.organisationId;

      const response = await AppService.get(
        `api/channel-partners/${partnerId}/commission/monthly-statement`
      );
      setStatement(response);
      setError(null);
    } catch (err) {
      console.error("Error fetching monthly statement:", err);
      setError("Failed to load monthly statement");
      setStatement(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PAID":
        return "success";
      case "PARTIALLY_PAID":
        return "info";
      case "PENDING_PAYMENT":
        return "warning";
      case "PENDING_APPROVAL":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "PAID":
        return "Paid";
      case "PARTIALLY_PAID":
        return "Partially Paid";
      case "PENDING_PAYMENT":
        return "Pending Payment";
      case "PENDING_APPROVAL":
        return "Pending Approval";
      default:
        return status || "Unknown";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : !statement ? (
        <Alert severity="info">No statement data available</Alert>
      ) : (
        <Grid container spacing={3}>
          {/* Header Card */}
          <Grid item xs={12}>
            <Card sx={{ p: 3, background: "linear-gradient(135deg, #1a7f8f 0%, #2a9faf 100%)" }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={8}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CalendarMonth sx={{ color: 'white', fontSize: 28 }} />
                    <Typography color="white" variant="h5" sx={{ fontWeight: "bold" }}>
                      {statement.period}
                    </Typography>
                  </Box>
                  <Typography color="white" variant="body2" sx={{ opacity: 0.9 }}>
                    Monthly Commission Statement
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} sx={{ textAlign: { sm: "right" } }}>
                  <Chip
                    label={getStatusLabel(statement.status)}
                    color={getStatusColor(statement.status)}
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      py: 0.5
                    }}
                  />
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* Summary Tiles */}
          <Grid item xs={12} sm={6} md={3}>
            <MetricTile
              title="New Referrals"
              amount={statement.newReferrals || 0}
              subtitle="This month"
              icon={People}
              color="#1a7f8f"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricTile
              title="Commission Rate"
              amount={`${statement.commissionRate || 0}%`}
              subtitle="Current rate"
              icon={Payments}
              color="#16697A"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricTile
              title="Paid Commission"
              amount={formatCurrency(statement.paidCommission)}
              subtitle={`${statement.paidTicketsCount || 0} ticket${statement.paidTicketsCount !== 1 ? 's' : ''} paid`}
              icon={CheckCircle}
              color="#2e7d32"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricTile
              title="Pending Commission"
              amount={formatCurrency(statement.pendingCommission)}
              subtitle={`${statement.pendingTicketsCount || 0} ticket${statement.pendingTicketsCount !== 1 ? 's' : ''} pending`}
              icon={Pending}
              color="#ed6c02"
            />
          </Grid>

          {/* Statement Details */}
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 2 }}>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight={600}>
                  Statement Summary
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Tickets This Month
                      </Typography>
                      <Typography variant="h5" fontWeight={600}>
                        {statement.totalTicketsThisMonth || 0}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Commission Earned
                      </Typography>
                      <Typography variant="h5" fontWeight={600} color="primary.main">
                        {formatCurrency(statement.totalCommissionEarned)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Expected Payment Date
                      </Typography>
                      <Typography variant="h6" fontWeight={500}>
                        {statement.expectedPaymentDate || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Payment Method
                      </Typography>
                      <Typography variant="h6" fontWeight={500}>
                        {statement.paymentMethod || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>

          {/* Bank Account Info */}
          {statement.bankAccount && (
            <Grid item xs={12}>
              <Card sx={{ p: 2, bgcolor: '#f5f9fa', border: '1px solid #e0e0e0' }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <AccountBalance sx={{ color: '#1a7f8f', fontSize: 28 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Bank Account for Payment
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {statement.bankAccount}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          )}

          {/* Service Breakdown */}
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 2 }}>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight={600}>
                  Commission Breakdown by Service
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Detailed breakdown of commissions earned per service
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#1a8a7e' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Service Name</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Tickets Count</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Commission Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statement.breakdown && statement.breakdown.length > 0 ? (
                      <>
                        {statement.breakdown.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography fontWeight={500}>{item.serviceName}</Typography>
                            </TableCell>
                            <TableCell align="right">{item.ticketsCount}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#1a7f8f' }}>
                              {formatCurrency(item.commissionAmount)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Total Row */}
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell>
                            <Typography fontWeight={700}>Total</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={700}>
                              {statement.breakdown.reduce((sum, item) => sum + (item.ticketsCount || 0), 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={700} color="primary.main">
                              {formatCurrency(statement.breakdown.reduce((sum, item) => sum + (parseFloat(item.commissionAmount) || 0), 0))}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No service breakdown available</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default MonthlyStatement;
